import React from 'react';
import Helmet from 'react-helmet';
import { makeStyles } from '@material-ui/core';

import MuiBreadcrumbs from 'ha/ui/next/Breadcrumbs';
import { useIntl } from 'ha/i18n';
import { notEmpty } from 'ha/utils/notEmpty';

import ArrowRightIcon from 'streamline-icons/arrow-right-1-bold.svg';
import HousingAnywhereIcon from 'custom-icons/housing-anywhere-icon.svg';

import { Breadcrumb } from './types';
import BreadcrumbItem from './BreadcrumbItem';

interface Props {
  breadcrumbs: Array<Breadcrumb | undefined>;
  withJsonLDSchema?: boolean;
}

const getJsonLdBreadcrumbs = (breadcrumbs: Breadcrumb[]) =>
  JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map(
      ({ name, schemaName = name, path, schemaPath = path }, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': schemaPath,
          name: schemaName,
        },
      }),
    ),
  });

export const useStyles = makeStyles(t => ({
  ol: {
    whiteSpace: 'nowrap',
    flexWrap: 'initial',
  },
}));

const Breadcrumbs: React.FC<Props> = ({
  breadcrumbs,
  withJsonLDSchema = true,
}) => {
  const filteredBreadcrumbs = breadcrumbs.filter(notEmpty);

  const { T, urlResolver } = useIntl();
  const classes = useStyles();

  return (
    <>
      {withJsonLDSchema && (
        <Helmet>
          <script type="application/ld+json">
            {getJsonLdBreadcrumbs([
              { name: T('Home'), path: urlResolver.getBaseUrl() },
              ...filteredBreadcrumbs,
            ])}
          </script>
        </Helmet>
      )}
      <MuiBreadcrumbs
        separator={<ArrowRightIcon fontSize="xs" />}
        classes={{
          ol: classes.ol,
        }}
        data-test-locator="Breadcrumbs"
      >
        <BreadcrumbItem
          text={<HousingAnywhereIcon fontSize="inherit" />}
          url={urlResolver.getHomeUrl()}
          renderAsLink
        />
        {filteredBreadcrumbs.map((breadcrumb, index) => (
          <BreadcrumbItem
            key={index}
            text={breadcrumb.name}
            url={breadcrumb.path}
            renderAsLink={index !== breadcrumbs.length - 1}
          />
        ))}
      </MuiBreadcrumbs>
    </>
  );
};

export default Breadcrumbs;
